import { observer } from "mobx-react";
import React, {
    CSSProperties,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import {
    Alert,
    Box,
    Checkbox,
    ClickAwayListener,
    Divider,
    FormControlLabel,
    Grid,
    IconButton,
    Paper,
    Popper,
    Typography,
} from "@mui/material";
import DownChevronSvg from "./BaseSelectComponents/DownChevronSvg";
import AcxButton from "../AcxButton";
import AcxMainTextField from "../AcxMainTextField";
import ClearIcon from "@mui/icons-material/Clear";
import theme from "Theme/AppTheme";
import CircularProgress from "@mui/material/CircularProgress";
import { FixedSizeList as List } from "react-window";
import _ from "lodash";

interface Props<T> {
    id: string;
    onApply?: (selectedIds: string[]) => void;
    defaultSelectedIds?: string[];
    maxSelectionCount?: number;
    warningMessage?: string;
    options: T[]; // options must have a link to parent groupings, otherwise fall into unassigned
    idField?: string;
    labelField?: string;
    groupingIdField?: string;
    groupingLabelField?: string;
    isLoading?: boolean;
    itemName?: string;
}

interface IGroupItem<T> {
    id: string;
    label: string;
    children: T[];
}

interface IFlattenedItem<T> {
    id: string;
    label: string;
    itemType: "group" | "item";
    parentId: string | undefined;
    children: T[];
}

const areSelectedIdsEqual = (
    selectedIds: string[],
    defaultSelectedIds: string[] | undefined,
) => _.isEqual([...selectedIds].sort(), [...(defaultSelectedIds ?? [])].sort());

const AcxVirtualizedSelectMultiGrouped = observer(<T,>(props: Props<T>) => {
    const [isOpen, setIsOpen] = useState(false);

    const [selectedIds, setSelectedIds] = useState<string[]>(
        props.defaultSelectedIds ?? [],
    );
    const currentSelectedIds = props.defaultSelectedIds ?? selectedIds;

    const maxSelected = props.maxSelectionCount
        ? currentSelectedIds.length >= props.maxSelectionCount
        : false;

    const [localFormattedGroups, setLocalFormattedGroups] = useState<
        IGroupItem<T>[]
    >([]);

    const [searchTerm, setSearchTerm] = useState("");

    const ref = useRef<HTMLDivElement | null>(null);

    const searchInputRef = useRef<HTMLInputElement | null>(null);

    const clientWidth = ref.current?.clientWidth ?? 400;

    const localIdField = props.idField ?? "id";
    const localLabelField = props.labelField ?? "label";
    const localGroupingIdField = props.groupingIdField ?? "managerId";
    const localGroupingLabelField =
        props.groupingLabelField ?? "managerFullName";

    const itemName = props.itemName ?? "Item";

    const selectedCountLabel = `${currentSelectedIds.length} ${
        currentSelectedIds.length === 1 ? itemName : itemName + "s"
    } Selected`;

    const handleOpen: React.MouseEventHandler<HTMLDivElement> | undefined =
        () => {
            const sortedGroups = sortGroupings(
                localFormattedGroups,
                currentSelectedIds,
            );
            const finalFormattedGroups = sortChildren(
                sortedGroups,
                currentSelectedIds,
            );
            setLocalFormattedGroups(finalFormattedGroups);
            setIsOpen((prev) => !prev);
        };

    const applySelectedIds = (newSelectedIds: string[]) => {
        if (areSelectedIdsEqual(currentSelectedIds, newSelectedIds)) return;

        setSelectedIds(newSelectedIds);
        props.onApply?.(newSelectedIds);
    };

    const handleChange = (
        val: React.ChangeEvent<HTMLInputElement>,
        option: IFlattenedItem<T>,
    ) => {
        if (option.itemType === "group") {
            let newSelectedIds: string[] = [];

            const parentId = option.id.toString();
            let childIds: string[] = [];

            if (option.label === "Unassigned") {
                childIds = props.options
                    .filter((i) => !i[localGroupingIdField])
                    .map((i) => i[localIdField]);
            } else {
                childIds = props.options
                    .filter((i) => i[localGroupingIdField] === parentId)
                    .map((i) => i[localIdField]);
            }

            if (val.target.checked) {
                const max = props.maxSelectionCount ?? Infinity;

                const remaining = max - currentSelectedIds.length;

                // we disable when at maxSelection so this is just a safety check
                if (remaining <= 0) return;
                const unselected = childIds.filter(
                    (id) => !currentSelectedIds.includes(id),
                );

                const newlyAddedIds = unselected.slice(0, remaining);
                newSelectedIds = [...currentSelectedIds, ...newlyAddedIds];
            } else {
                newSelectedIds = currentSelectedIds.filter(
                    (i) => !childIds.includes(i),
                );
            }

            applySelectedIds(newSelectedIds);
        } else {
            let newSelectedIds: string[] = [];

            const optionId = option[localIdField].toString();

            if (val.target.checked) {
                newSelectedIds = [...currentSelectedIds, optionId];
            } else {
                newSelectedIds = currentSelectedIds.filter(
                    (i) => i !== optionId,
                );
            }

            applySelectedIds(newSelectedIds);
        }
    };

    const handleSelectAll = () => {
        const setCopy = new Set(currentSelectedIds);
        const max = props.maxSelectionCount ?? Infinity;

        const remaining = max - currentSelectedIds.length;

        // we disable when at maxSelection so this is just a safety check
        if (remaining <= 0) return;

        // filter out already-selected IDs
        const unselected = filteredIds.filter((id) => !setCopy.has(id));

        const newlyAddedIds = unselected.slice(0, remaining);

        applySelectedIds([...currentSelectedIds, ...newlyAddedIds]);
    };

    const handleClickAway = () => {
        setIsOpen((prev) => !prev);
    };

    // clear only selected ids from currently filtered items
    const handleClear = () => {
        const filterIdSet = new Set(filteredIds);
        const newSelectedIds = currentSelectedIds.filter(
            (i) => !filterIdSet.has(i),
        );

        applySelectedIds(newSelectedIds);
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (event.code === "Escape") {
            setIsOpen(false);
        } else if (event.code === "Space") {
            // only want to open on space press, not close
            // other selects close when pressing space because it also selects and they close on select
            // this select does not close on select by design
            setIsOpen(true);
        }
    };

    useEffect(() => {
        const sortedGroups = sortChildren(localFormattedGroups, selectedIds);
        setLocalFormattedGroups(sortedGroups);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedIds]);

    useEffect(() => {
        if (isOpen && searchInputRef.current) {
            searchInputRef.current?.focus();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, searchInputRef.current]);

    // keep selectedIds updated when external controlled value changes (e.g. clearing filters)
    useEffect(() => {
        if (!areSelectedIdsEqual(selectedIds, props.defaultSelectedIds)) {
            setSelectedIds(props.defaultSelectedIds ?? []);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.defaultSelectedIds]);

    const formattedGroupings: IGroupItem<T>[] = useMemo(() => {
        const optionGroupingIds = props.options
            .filter((o) => !!o[localGroupingIdField])
            .map((o) => {
                return o[localGroupingIdField];
            });

        const uniqueGroupingIds: string[] = Array(
            ...new Set(optionGroupingIds),
        );

        const formattedGroups = uniqueGroupingIds.map((g) => {
            const foundOptionForGroupId = props.options.find(
                (o) => o[localGroupingIdField] === g,
            );

            const label = foundOptionForGroupId?.[localGroupingLabelField];

            return {
                id: g,
                label: label,
                children: props.options.filter(
                    (o) => o[localGroupingIdField] === g,
                ),
            };
        });

        formattedGroups.sort((a, b) => {
            return (a.label as string)?.localeCompare(b.label) ?? 0;
        });

        const unassignedOptions = props.options.filter(
            (o) => !o[localGroupingIdField],
        );

        if (unassignedOptions.length > 0) {
            formattedGroups.push({
                id: "Unassigned",
                label: "Unassigned",
                children: unassignedOptions,
            });
        }

        return formattedGroups;
    }, [props.options, localGroupingIdField, localGroupingLabelField]);

    // update options when loaded
    useEffect(() => {
        setLocalFormattedGroups(formattedGroupings);
    }, [formattedGroupings]);

    const sortChildren = (groups: IGroupItem<T>[], selectedIds: string[]) => {
        const selectedSet = new Set(selectedIds);

        // Clone and sort groups
        return groups.map((group) => ({
            ...group,
            children: [...group.children].sort((a, b) => {
                const aSel = selectedSet.has(a[localIdField]) ? 1 : 0;
                const bSel = selectedSet.has(b[localIdField]) ? 1 : 0;
                return bSel - aSel; // selected first
            }),
        }));
    };

    const sortGroupings = (groups: IGroupItem<T>[], selectedIds: string[]) => {
        const selectedSet = new Set(selectedIds);

        // Helper: number of selected children
        const countSelected = (items: T[]) =>
            items.reduce(
                (acc, item) =>
                    acc + (selectedSet.has(item[localIdField]) ? 1 : 0),
                1,
            );

        // sort groups
        return groups.sort((a, b) => {
            const aCount = countSelected(a.children);
            const bCount = countSelected(b.children);
            return bCount - aCount; // groups with more selected children first
        });
    };

    const flattenGroups = (groups: IGroupItem<T>[]) => {
        const result: IFlattenedItem<T>[] = [];

        for (const group of groups) {
            // Add the group header
            result.push({
                itemType: "group",
                id: group.id,
                label: group.label,
                children: group.children,
                parentId: undefined,
            });

            // Add all children as item rows
            for (const item of group.children) {
                result.push({
                    itemType: "item",
                    parentId: group.id,
                    label: item[localLabelField],
                    id: item[localIdField],
                    children: [],
                });
            }
        }

        return result;
    };

    const flattened: IFlattenedItem<T>[] = flattenGroups(localFormattedGroups);

    const filteredOptions: IFlattenedItem<T>[] = useMemo(() => {
        return flattened.filter(
            (o) =>
                o?.label?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                o.children?.some((i) =>
                    i[localLabelField]
                        ?.toLowerCase()
                        .includes(searchTerm.toLowerCase()),
                ),
        );
    }, [flattened, searchTerm, localLabelField]);

    const filteredIds: string[] = useMemo(() => {
        return filteredOptions
            .filter((o) => o.itemType !== "group")
            .map((o) => o[localIdField].toString());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [localIdField, filteredOptions]);

    const checkboxItem = (checkboxProps: {
        isSelected: boolean;
        isIndeterminate: boolean;
        style: React.CSSProperties;
        option: IFlattenedItem<T>;
        paddingLeft: string;
    }) => {
        const { isSelected, isIndeterminate, style, option, paddingLeft } =
            checkboxProps;
        const optionLabel = option.label;
        const optionId = option.id.toString();
        return (
            <Grid
                container
                item
                xs={12}
                id={props.id + "-option-" + optionId}
                sx={{
                    backgroundColor: isSelected
                        ? theme.palette.lightBlue
                        : theme.palette.white.main,
                    paddingLeft: paddingLeft,
                    paddingRight: theme.spacing(1),
                    height: "fit-content",
                    "&:hover": {
                        backgroundColor: theme.palette.lightBlue,
                    },
                }}
                style={style}
                key={optionId}
                margin={theme.spacing(0.1)}
            >
                <FormControlLabel
                    control={
                        <Checkbox
                            onChange={(val) => handleChange(val, option)}
                            name={optionLabel}
                            checked={isSelected}
                            sx={{
                                padding: theme.spacing(0.5),
                            }}
                            indeterminate={isIndeterminate}
                            disabled={!isSelected && maxSelected}
                        />
                    }
                    label={
                        <Box
                            width={clientWidth - 40}
                            height={32}
                            display={"flex"}
                            alignItems={"center"}
                            sx={
                                isSelected
                                    ? { color: theme.palette.primary.main }
                                    : {
                                          "&:hover": {
                                              color:
                                                  !isSelected && maxSelected
                                                      ? null
                                                      : theme.palette.primary
                                                            .main,
                                          },
                                      }
                            }
                        >
                            <Typography
                                noWrap
                                variant="inherit"
                                title={optionLabel}
                                sx={{ minWidth: 0 }}
                            >
                                {optionLabel}
                            </Typography>
                        </Box>
                    }
                    style={{
                        overflowWrap: "anywhere",
                    }}
                    labelPlacement={"end"}
                />
            </Grid>
        );
    };

    const renderCheckboxItem = (
        option: IFlattenedItem<T>,
        style: CSSProperties,
    ) => {
        if (option.itemType === "group") {
            const allChildrenSelected = option.children
                .map((i) => i[localIdField])
                .every((id) => selectedIds.includes(id));

            const isSelected = allChildrenSelected;

            const someChildrenSelected = option.children
                .map((i) => i[localIdField])
                .some((id) => selectedIds.includes(id));

            const isIndeterminate =
                !allChildrenSelected && someChildrenSelected;

            return checkboxItem({
                isSelected,
                isIndeterminate,
                option,
                style,
                paddingLeft: theme.spacing(1.5),
            });
        } else {
            const isSelected = selectedIds.includes(option.id.toString());

            return checkboxItem({
                isSelected,
                isIndeterminate: false,
                option,
                style,
                paddingLeft: theme.spacing(4),
            });
        }
    };

    const searchInputIcon = props.isLoading ? (
        <Grid
            style={{
                paddingRight: theme.spacing(1),
            }}
        >
            <CircularProgress size={18} />
        </Grid>
    ) : (
        <IconButton size="small" onClick={() => setSearchTerm("")}>
            <ClearIcon fontSize="small" />
        </IconButton>
    );

    const searchInput = (
        <Grid
            item
            xs={12}
            style={{
                padding: theme.spacing(2),
                paddingBottom: 0,
            }}
        >
            <AcxMainTextField
                id={props.id + "-search-compact-select-multi"}
                value={searchTerm}
                placeholderText={"Search..."}
                onChange={(e) => setSearchTerm(e.target.value)}
                endAdornment={searchInputIcon}
                fullWidth
                isDisabled={props.isLoading}
                focus
                ref={searchInputRef}
            />
        </Grid>
    );

    const headerButtons = (
        <Grid container justifyContent="space-between" alignItems="center">
            <Grid item margin={theme.spacing(1)}>
                <AcxButton
                    fullWidth
                    id={props.id + "-apply-button"}
                    color="primary"
                    variant="text"
                    onClick={handleSelectAll}
                    disabled={maxSelected}
                >
                    Select All{" " + itemName + "s"}
                </AcxButton>
            </Grid>
            <Grid item margin={theme.spacing(1)}>
                <AcxButton
                    fullWidth
                    id={props.id + "-clear-button"}
                    onClick={handleClear}
                    color="white"
                    variant="text"
                >
                    Clear
                </AcxButton>
            </Grid>
        </Grid>
    );

    const virtualizedList = (
        <List
            itemCount={filteredOptions.length}
            itemSize={32}
            itemData={filteredOptions}
            height={300}
            width={clientWidth}
            style={{
                overflowX: "hidden",
                borderBottomRightRadius: theme.shape.borderRadius,
                borderBottomLeftRadius: theme.shape.borderRadius,
            }}
        >
            {({ index, style }) => {
                return renderCheckboxItem(filteredOptions[index], style);
            }}
        </List>
    );

    const popperComponent = (
        <ClickAwayListener onClickAway={handleClickAway}>
            <Popper
                open={isOpen}
                anchorEl={ref.current}
                placement="bottom"
                style={{
                    zIndex: theme.zIndex.modal,
                }}
                onKeyDown={handleKeyDown}
            >
                <Paper
                    style={{
                        width: clientWidth,
                        boxShadow: theme.shadows[2],
                        marginTop: theme.spacing(1),
                        overflow: "hidden",
                    }}
                >
                    {searchInput}

                    {props.warningMessage &&
                        props.maxSelectionCount &&
                        selectedIds.length === props.maxSelectionCount && (
                            <Alert sx={{ mx: 2, mt: 1 }} severity="warning">
                                {props.warningMessage}
                            </Alert>
                        )}

                    {headerButtons}

                    <Divider />

                    {virtualizedList}
                </Paper>
            </Popper>
        </ClickAwayListener>
    );

    return (
        <>
            <Grid
                container
                justifyContent="space-between"
                onClick={handleOpen}
                sx={{
                    backgroundColor: theme.palette.white.main,
                    fontSize: "13px",
                    border: "0.5px solid",
                    borderColor: isOpen
                        ? theme.palette.primary.main
                        : theme.palette.lightgrayBorder.main,
                    borderRadius: 1,
                    minHeight: "32px",
                    height: "32px",
                    cursor: "pointer",
                    padding: "6px",
                    paddingLeft: "8px",
                }}
                ref={ref}
                id={props.id}
                wrap="nowrap"
                onKeyDown={handleKeyDown}
                tabIndex={0}
            >
                <Typography noWrap>{selectedCountLabel}</Typography>

                <Grid item>
                    <DownChevronSvg />
                </Grid>
            </Grid>
            {isOpen && popperComponent}
        </>
    );
});

export default AcxVirtualizedSelectMultiGrouped;
